import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { ContractDocument } from "@/components/onboarding/ContractDocument";
import { colors, fonts, gradients, radius, spacing, typography } from "@/lib/theme";

interface ContractResignProps {
    /** The real agreement markdown (from the "Contract text" item). */
    markdown: string;
    /** The current contract is signed; show the confirmation, not the sign step. */
    signed: boolean;
    /** Display date of the signature ("Oct 7, 2026"), when known. */
    signedOn: string | null;
    /** Opens the signature pad. Confirming the pad submits right away. */
    onSign: () => void;
    submitting: boolean;
    submitError: string | null;
}

/**
 * Read-and-sign renderer for the RN employment contract on the Compliance
 * screen. Shows the real agreement (the same collapsible reader onboarding
 * uses) and ONE action: "Sign & submit" opens the pad, and confirming the pad
 * saves. The server regenerates the real executed PDF and mints a fresh,
 * non-expiring compliance record.
 *
 * It used to be two steps (draw, then a separate submit button), and the first
 * step ended on a green "Signed" check. Nurses read that as done and left, so
 * nothing was saved (Marlene Benjamin, 4144, tried five times). It also looked
 * the same after a successful submit, so others signed again and again. Once
 * signed, the screen now says so and offers nothing to tap.
 */
export function ContractResign({
    markdown,
    signed,
    signedOn,
    onSign,
    submitting,
    submitError
}: ContractResignProps) {
    if (signed) {
        return (
            <View style={styles.container}>
                <View style={styles.signedCard}>
                    <MaterialCommunityIcons name="check-circle" size={28} color={colors.teal600} />
                    <View style={styles.signedBody}>
                        <Text style={styles.signedTitle}>Contract signed</Text>
                        <Text style={styles.signedSub}>
                            {signedOn ? `Signed ${signedOn}. ` : ""}Nothing else to do here.
                        </Text>
                    </View>
                </View>
                <ContractDocument markdown={markdown} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <ContractDocument markdown={markdown} />

            <View style={styles.signBlock}>
                <Text style={styles.signIntro}>
                    By signing below you confirm you have read and agree to this agreement.
                </Text>
                <Pressable onPress={onSign} disabled={submitting} style={styles.primaryWrap}>
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={[styles.primaryBtn, submitting && styles.primaryDisabled]}
                    >
                        {!submitting ? (
                            <MaterialCommunityIcons name="draw" size={18} color={colors.surface} />
                        ) : null}
                        <Text style={styles.primaryText}>
                            {submitting ? "Submitting…" : "Sign & submit"}
                        </Text>
                    </LinearGradient>
                </Pressable>
                {submitError ? (
                    <View>
                        <Text style={styles.error}>
                            Your signature was not saved. Please try again.
                        </Text>
                        <Text style={styles.errorDetail}>{submitError}</Text>
                    </View>
                ) : null}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: spacing.base
    },
    signedCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        padding: spacing.base,
        borderRadius: radius.lg,
        backgroundColor: colors.teal50,
        borderWidth: 1,
        borderColor: colors.teal200
    },
    signedBody: {
        flex: 1,
        gap: 2
    },
    signedTitle: {
        ...typography.subtitle,
        color: colors.teal700
    },
    signedSub: {
        ...typography.body,
        color: colors.textSecondary
    },
    signBlock: {
        gap: spacing.md,
        padding: spacing.base,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        backgroundColor: colors.surface
    },
    signIntro: {
        ...typography.body,
        color: colors.textSecondary
    },
    error: {
        ...typography.caption,
        color: colors.error
    },
    errorDetail: {
        ...typography.caption,
        color: colors.textTertiary
    },
    primaryWrap: {
        alignSelf: "stretch"
    },
    primaryBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        borderRadius: radius.full,
        paddingVertical: 14
    },
    primaryDisabled: {
        opacity: 0.5
    },
    primaryText: {
        fontFamily: fonts.bold,
        fontSize: 15,
        color: colors.surface
    }
});
